import * as React from "react";

import { useTheme, type Theme, type ThemeOrigin } from "context/ThemeContext";
import { cn } from "utils/cn";
import { Moon, Sun } from "./icons";
import { Tooltip } from "./Tooltip";

// A slight overshoot, so the knob and the highlight settle like a physical switch.
const SPRING = "ease-[cubic-bezier(0.34,1.4,0.64,1)]";

/** The centre of the control that was pressed: the new theme grows from there. */
const originOf = (event: React.MouseEvent<HTMLElement>): ThemeOrigin => {
  const box = event.currentTarget.getBoundingClientRect();
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
};

/** Sun and moon stacked in one spot; the one not in use turns away and shrinks. */
const ThemeIcon: React.FC<{ dark: boolean; className?: string }> = ({ dark, className }) => (
  <span aria-hidden="true" className={cn("relative block h-4 w-4", className)}>
    <Sun
      weight="fill"
      className={cn(
        "absolute inset-0 h-4 w-4 text-warning transition-all duration-700",
        SPRING,
        dark ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
      )}
    />
    <Moon
      weight="fill"
      className={cn(
        "absolute inset-0 h-4 w-4 text-primary-text transition-all duration-700",
        SPRING,
        dark ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0"
      )}
    />
  </span>
);

export interface ThemeToggleProps {
  /**
   * `switch`: a compact sliding switch (top bar). `segmented`: "Light | Dark" with labels, for
   * places where the choice should stand out (the sign-in pages).
   */
  variant?: "switch" | "segmented";
  className?: string;
}

const OPTIONS: Array<{ value: Theme; label: string }> = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

/** Light or dark. Light is the default; signed-in users keep their choice on their account. */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ variant = "switch", className }) => {
  const { theme, setTheme, toggleTheme } = useTheme();
  const dark = theme === "dark";

  if (variant === "segmented") {
    return (
      <div
        role="radiogroup"
        aria-label="Theme"
        data-ui="theme-toggle"
        className={cn(
          "relative inline-flex items-center rounded-full border border-border bg-surface-muted p-1 shadow-xs",
          className
        )}
      >
        {/* The highlight slides under the chosen option. */}
        <span
          aria-hidden="true"
          className={cn(
            "absolute left-1 top-1 h-8 w-[5.5rem] rounded-full border border-border bg-surface shadow-card transition-transform duration-700",
            SPRING
          )}
          style={{ transform: dark ? "translateX(100%)" : "translateX(0)" }}
        />
        {OPTIONS.map((option) => {
          const selected = option.value === theme;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={(event) => {
                if (!selected) setTheme(option.value, originOf(event));
              }}
              className={cn(
                "relative z-10 inline-flex h-8 w-[5.5rem] items-center justify-center gap-2 rounded-full text-sm font-medium transition-colors duration-base",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {option.value === "light" ? (
                <Sun
                  aria-hidden="true"
                  weight={selected ? "fill" : "regular"}
                  className={cn(
                    "h-4 w-4 transition-transform duration-700",
                    SPRING,
                    selected ? "rotate-0 scale-110 text-warning" : "-rotate-45 scale-100"
                  )}
                />
              ) : (
                <Moon
                  aria-hidden="true"
                  weight={selected ? "fill" : "regular"}
                  className={cn(
                    "h-4 w-4 transition-transform duration-700",
                    SPRING,
                    selected ? "rotate-0 scale-110 text-primary-text" : "rotate-45 scale-100"
                  )}
                />
              )}
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  const label = dark ? "Switch to light theme" : "Switch to dark theme";
  return (
    <Tooltip content={label}>
      <button
        type="button"
        role="switch"
        aria-checked={dark}
        aria-label="Dark theme"
        data-ui="theme-toggle"
        onClick={(event) => toggleTheme(originOf(event))}
        className={cn(
          "relative inline-flex h-8 w-14 shrink-0 items-center rounded-full border p-0.5 transition-colors duration-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          dark ? "border-primary/30 bg-primary/20" : "border-border bg-surface-muted",
          className
        )}
      >
        {/* Faint marks on the track for the side the knob isn't on. */}
        <Sun
          aria-hidden="true"
          className={cn(
            "absolute left-2 h-3 w-3 text-muted-foreground transition-opacity duration-700",
            dark ? "opacity-60" : "opacity-0"
          )}
        />
        <Moon
          aria-hidden="true"
          className={cn(
            "absolute right-2 h-3 w-3 text-muted-foreground transition-opacity duration-700",
            dark ? "opacity-0" : "opacity-60"
          )}
        />
        <span
          className={cn(
            "relative flex h-[1.625rem] w-[1.625rem] items-center justify-center rounded-full bg-surface shadow-card transition-transform duration-700",
            SPRING
          )}
          style={{ transform: dark ? "translateX(1.5rem)" : "translateX(0)" }}
        >
          <ThemeIcon dark={dark} className="h-3.5 w-3.5 [&>svg]:h-3.5 [&>svg]:w-3.5" />
        </span>
      </button>
    </Tooltip>
  );
};
